import { Controller } from "@hotwired/stimulus";
import * as Turbo from "@hotwired/turbo";

export default class extends Controller {
  static targets = [
    "rowContainer",
    "row",
    "emptyState",
    "count",
    "loadDefaultsButton",
    "rowTotal",
    "quantityInput",
    "requestError",
    "requestErrorMessage",
    "notice",
    "noticeMessage",
    "busySpinner",
    "addSupply",
    "submitButton",
    "lotsRequired",
  ];
  static values = {
    createUrl: String,
    defaultsUrl: String,
    refreshUrl: String,
    pickerUrl: String,
    containerId: String,
    comboboxId: String,
    fieldCycleId: String,
    requestError: String,
    noDefaults: String,
  };

  #busy = false;
  #generation = 0;
  #pendingRefresh = false;
  #lastRetry = null;
  #abortController = null;

  connect() {
    this.#syncLoadDefaultsButton();
    this.#syncLotsGate();
  }

  disconnect() {
    clearTimeout(this.refreshTimer);
    this.#abortController?.abort();
  }

  async loadDefaults(fieldTaskId) {
    this.#pendingRefresh = false;
    if (!fieldTaskId) {
      this.#abortInFlight();
      this.#clearAll();
      return;
    }

    this.#lastRetry = () => this.loadDefaults(fieldTaskId);
    await this.#withBusy(async (signal) => {
      const response = await this.#turboStreamRequest(
        this.defaultsUrlValue,
        {
          field_task_id: fieldTaskId,
          field_cycle_id: this.#fieldCycleId(),
          existing_supply_ids: this.#existingSupplyIds(),
          lot_ids: this.#selectedLotIds(),
          container_id: this.containerIdValue,
          combobox_id: this.comboboxIdValue,
        },
        signal,
      );
      if (!response) return;

      if (!response.ok) {
        this.#showRequestError();
        return;
      }

      await Turbo.renderStreamMessage(await response.text());
      this.#openSuppliesDisclosure();
      this.#syncStateDeferred();
      requestAnimationFrame(() => {
        if (this.rowTargets.length === 0) this.#showNoDefaultsNotice();
      });
    });
  }

  loadDefaultSupplies(event) {
    event.preventDefault();
    if (this.#busy || this.rowTargets.length > 0) return;

    const fieldTaskId = this.#fieldTaskId();
    if (!fieldTaskId) return;

    this.loadDefaults(fieldTaskId);
  }

  fieldTaskChanged() {
    this.#syncLoadDefaultsButton();
  }

  async addSupply(event) {
    const supplyId = event?.detail?.value;
    if (!supplyId || this.#busy || this.#isSupplyAdded(supplyId)) return;
    if (this.#lotsGated()) return;

    this.#lastRetry = () => this.addSupply(event);
    await this.#withBusy(async (signal) => {
      const response = await this.#turboStreamRequest(
        this.createUrlValue,
        {
          supply_id: supplyId,
          field_task_id: this.#fieldTaskId(),
          field_cycle_id: this.#fieldCycleId(),
          existing_supply_ids: this.#existingSupplyIds(),
          lot_ids: this.#selectedLotIds(),
          container_id: this.containerIdValue,
          combobox_id: this.comboboxIdValue,
        },
        signal,
      );
      if (!response) return;

      if (!response.ok) {
        this.#showRequestError();
        return;
      }

      await Turbo.renderStreamMessage(await response.text());
      this.#hideNotice();
      this.#openSuppliesDisclosure();
      this.#syncStateDeferred();
    });
  }

  async retryLastRequest(event) {
    event.preventDefault();
    if (!this.#lastRetry) return;

    await this.#lastRetry();
  }

  async removeRow(event) {
    const row = event.target.closest("[data-supply-rows-target='row']");
    if (!row) return;

    row.remove();
    this.#syncState();
    await this.#refreshPicker();
  }

  refreshLots() {
    this.#syncLotsGate();
    this.#scheduleRefresh();
  }

  selectLotBlock(event) {
    event.preventDefault();
    const lotIds = this.#parseLotIdsParam(event.params.lotIds);
    if (lotIds.length === 0) return;

    const selected = new Set(lotIds.map(String));
    this.#lotCheckboxes().forEach((checkbox) => {
      if (!selected.has(String(checkbox.value)) || checkbox.checked) return;

      checkbox.checked = true;
      checkbox.dispatchEvent(new Event("change", { bubbles: true }));
    });

    this.#scheduleRefresh();
  }

  massFillSame(event) {
    const row = event.target.closest("[data-supply-rows-target='row']");
    if (!row) return;

    const value = this.#massFillValue(row);
    if (value === "") return;

    this.#quantityInputs(row).forEach((input) => {
      input.value = value;
    });
    this.#updateRowTotalFor(row);
  }

  updateRowTotal(event) {
    const row = event.target.closest("[data-supply-rows-target='row']");
    if (row) this.#updateRowTotalFor(row);
  }

  #clearAll() {
    if (this.hasRowContainerTarget) this.rowContainerTarget.innerHTML = "";
    this.#syncState();
  }

  #syncStateDeferred() {
    requestAnimationFrame(() => this.#syncState());
  }

  #syncState() {
    this.#updateCount();
    this.#toggleEmptyState(this.rowTargets.length === 0);
    this.#syncLoadDefaultsButton();
  }

  #syncLoadDefaultsButton() {
    if (!this.hasLoadDefaultsButtonTarget) return;

    const show = this.rowTargets.length === 0 && Boolean(this.#fieldTaskId());
    this.loadDefaultsButtonTarget.classList.toggle("hidden", !show);
  }

  // Hosts that render a lotsRequired notice gate the picker on lots: a supply needs a lot
  // to hold a quantity, so the notice shows until an assignment row names one.
  #lotsGated() {
    return this.hasLotsRequiredTarget && this.#selectedLotIds().length === 0;
  }

  #syncLotsGate() {
    const gated = this.#lotsGated();
    if (this.hasAddSupplyTarget) {
      this.addSupplyTarget.classList.toggle("hidden", gated);
    }
    if (this.hasLotsRequiredTarget) {
      this.lotsRequiredTarget.classList.toggle("hidden", !gated);
    }
  }

  #isSupplyAdded(supplyId) {
    return this.rowTargets.some((row) => {
      const input = row.querySelector("input[data-supply-id]");
      return input?.dataset?.supplyId === String(supplyId);
    });
  }

  #toggleEmptyState(show) {
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.toggle("hidden", !show);
    }
  }

  #updateCount() {
    if (this.hasCountTarget) {
      this.countTarget.textContent = String(this.rowTargets.length);
    }
  }

  #fieldTaskId() {
    return (
      this.element.querySelector('input[name="supply_exit[field_task_id]"]')
        ?.value ||
      this.element
        .closest("form")
        ?.querySelector('input[name="field_cycle_line_item[field_task_id]"]')
        ?.value
    );
  }

  #fieldCycleId() {
    if (this.hasFieldCycleIdValue && this.fieldCycleIdValue) {
      return this.fieldCycleIdValue;
    }

    return this.element
      .closest("form")
      ?.querySelector('input[name="field_cycle_line_item[field_cycle_id]"]')
      ?.value;
  }

  #selectedLotIds() {
    const checkboxLots = this.#lotCheckboxes();
    if (checkboxLots.length > 0) {
      return checkboxLots
        .filter((checkbox) => checkbox.checked)
        .map((checkbox) => checkbox.value);
    }

    const form = this.element.closest("form") || this.element;
    return [
      ...new Set(
        Array.from(form.querySelectorAll("select[name*='[lot_id]']"))
          .map((select) => select.value)
          .filter(Boolean),
      ),
    ];
  }

  #lotCheckboxes() {
    return Array.from(
      this.element.querySelectorAll('input[name="supply_exit[lot_ids][]"]'),
    );
  }

  #existingSupplyIds() {
    return this.rowTargets
      .map((row) => row.querySelector("input[data-supply-id]")?.dataset?.supplyId)
      .filter(Boolean);
  }

  #quantityInputs(row) {
    return Array.from(
      row.querySelectorAll("[data-supply-rows-target='quantityInput']"),
    );
  }

  #massFillValue(row) {
    return row.querySelector("[data-mass-fill-input]")?.value ?? "";
  }

  #updateRowTotalFor(row) {
    const totalTarget = row.querySelector("[data-supply-rows-target='rowTotal']");
    if (!totalTarget) return;

    const total = this.#quantityInputs(row).reduce((sum, input) => {
      const value = Number.parseFloat(input.value);
      return sum + (Number.isNaN(value) ? 0 : value);
    }, 0);
    const stock = Number.parseFloat(row.dataset.stock);
    const overStock = !Number.isNaN(stock) && total > 0 && total > stock;

    totalTarget.classList.toggle("text-error", overStock);
    totalTarget.classList.toggle("text-base-content/60", !overStock);

    const valueEl = totalTarget.querySelector("[data-row-total-value]");
    if (valueEl) {
      valueEl.textContent = this.#formatRowTotal(total);
    }
  }

  #formatRowTotal(total) {
    const rounded = Math.round(total * 100) / 100;
    const locale = document.documentElement?.lang || "es";
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(
      rounded,
    );
  }

  #parseLotIdsParam(value) {
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return value.split(",").map((id) => id.trim()).filter(Boolean);
      }
    }
    return [];
  }

  #scheduleRefresh() {
    if (!this.refreshUrlValue) return;

    clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => this.#refreshRows(), 50);
  }

  async #refreshRows() {
    if (this.rowTargets.length === 0) return;
    if (this.#busy) {
      this.#pendingRefresh = true;
      return;
    }

    this.#lastRetry = () => this.#refreshRows();
    await this.#withBusy(async (signal) => {
      const rows = this.rowTargets.map((row) => {
        const supplyInput = row.querySelector("input[data-supply-id]");
        const quantities = {};
        this.#quantityInputs(row).forEach((input) => {
          if (input.dataset.lotId) quantities[input.dataset.lotId] = input.value;
        });

        return {
          supply_id: supplyInput?.dataset?.supplyId,
          quantities,
        };
      });

      const response = await this.#turboStreamRequest(
        this.refreshUrlValue,
        {
          lot_ids: this.#selectedLotIds(),
          field_task_id: this.#fieldTaskId(),
          field_cycle_id: this.#fieldCycleId(),
          rows,
          container_id: this.containerIdValue,
          combobox_id: this.comboboxIdValue,
        },
        signal,
      );
      if (!response) return;

      if (!response.ok) {
        this.#showRequestError();
        return;
      }

      await Turbo.renderStreamMessage(await response.text());
      this.#syncStateDeferred();
    });
  }

  async #withBusy(fn) {
    this.#generation += 1;
    const generation = this.#generation;
    this.#abortInFlight();
    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;

    this.#busy = true;
    this.#setBusyUI(true);
    this.#hideRequestError();
    try {
      await fn(signal);
    } catch (error) {
      if (error?.name === "AbortError") return;
      this.#showRequestError();
    } finally {
      if (generation === this.#generation) {
        this.#busy = false;
        this.#setBusyUI(false);
        if (this.#pendingRefresh) {
          this.#pendingRefresh = false;
          this.#refreshRows();
        }
      }
    }
  }

  #abortInFlight() {
    this.#abortController?.abort();
  }

  #setBusyUI(busy) {
    if (this.hasRowContainerTarget) {
      this.rowContainerTarget.setAttribute("aria-busy", busy ? "true" : "false");
    }
    if (this.hasBusySpinnerTarget) {
      this.busySpinnerTarget.classList.toggle("hidden", !busy);
    }
    if (this.hasLoadDefaultsButtonTarget) {
      this.loadDefaultsButtonTarget.toggleAttribute("disabled", busy);
      this.loadDefaultsButtonTarget.setAttribute(
        "aria-busy",
        busy ? "true" : "false",
      );
    }
    if (this.hasAddSupplyTarget) {
      this.addSupplyTarget.classList.toggle("pointer-events-none", busy);
      this.addSupplyTarget.classList.toggle("opacity-60", busy);
      this.addSupplyTarget.setAttribute("aria-busy", busy ? "true" : "false");
    }
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.toggleAttribute("disabled", busy);
    }
  }

  #showRequestError() {
    if (!this.hasRequestErrorTarget) return;

    if (this.hasRequestErrorMessageTarget && this.requestErrorValue) {
      this.requestErrorMessageTarget.textContent = this.requestErrorValue;
    }
    this.requestErrorTarget.classList.remove("hidden");
  }

  #hideRequestError() {
    if (this.hasRequestErrorTarget) {
      this.requestErrorTarget.classList.add("hidden");
    }
  }

  #showNoDefaultsNotice() {
    if (!this.hasNoticeTarget || !this.noDefaultsValue) return;

    if (this.hasNoticeMessageTarget) {
      this.noticeMessageTarget.textContent = this.noDefaultsValue;
    }
    this.noticeTarget.classList.remove("hidden");
  }

  #hideNotice() {
    if (this.hasNoticeTarget) {
      this.noticeTarget.classList.add("hidden");
    }
  }

  #openSuppliesDisclosure() {
    const details = this.element.closest("details");
    if (details && this.rowTargets.length > 0) details.open = true;
  }

  async #refreshPicker() {
    if (!this.pickerUrlValue) return;

    const response = await this.#turboStreamRequest(this.pickerUrlValue, {
      existing_supply_ids: this.#existingSupplyIds(),
      combobox_id: this.comboboxIdValue,
    });
    if (!response?.ok) return;

    await Turbo.renderStreamMessage(await response.text());
  }

  async #turboStreamRequest(url, params, signal) {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content;

    try {
      return await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify(params),
        signal,
      });
    } catch (error) {
      if (error?.name === "AbortError") return null;
      throw error;
    }
  }
}
